import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Fab, Paper, Typography, TextField, IconButton, 
  Avatar, Divider 
} from '@mui/material';
import { 
  Chat as ChatIcon, Close as CloseIcon, Send as SendIcon, 
  SmartToy as BotIcon 
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import api from '../api'; 

// Custom typing animation
const typing = keyframes`
  0% { transform: translateY(0px); opacity: 0.4; }
  50% { transform: translateY(-3px); opacity: 1; }
  100% { transform: translateY(0px); opacity: 0.4; }
`;

// Markdown Bold Formatter
const formatMessage = (text) => {
  if (typeof text !== 'string') return text;
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

const ChatbotApplet = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Dragging state - Default to bottom-right
  const [position, setPosition] = useState(() => {
    const saved = sessionStorage.getItem('chatbot_position');
    if (saved) {
      return JSON.parse(saved);
    }
    // Default: bottom-right corner
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    return { 
      x: windowWidth - 88, // 24px padding + 64px fab size
      y: windowHeight - 88 
    };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);

  // Set Session ID
  useEffect(() => {
    const currentSession = sessionStorage.getItem('ai_chat_session');
    if (currentSession) {
      setSessionId(currentSession);
    } else {
      const newSession = 'sess_' + Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem('ai_chat_session', newSession);
      setSessionId(newSession);
    }
  }, []);

  // Smart Initialization
  useEffect(() => {
    if (isOpen && !hasInitialized && sessionId) {
      setHasInitialized(true);
      setIsLoading(true);
      api.post('/chatbot/chat', { message: 'hi', session_id: sessionId })
        .then(res => {
          setMessages([{ role: 'assistant', content: res.data.reply }]);
        })
        .catch(err => {
          setMessages([{ role: 'assistant', content: 'Hi, how can I help you today?' }]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, hasInitialized, sessionId]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  // Auto-focus input when bot finishes loading
  useEffect(() => {
    if (!isLoading && isOpen) {
      inputRef.current?.focus();
    }
  }, [isLoading, isOpen]);

  // Save position when it changes
  useEffect(() => {
    sessionStorage.setItem('chatbot_position', JSON.stringify(position));
  }, [position]);

  // Handle drag start
  const handleDragStart = (e) => {
    if (e.button !== 0) return;
    const rect = fabRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  // Handle drag move
  useEffect(() => {
    const handleDragMove = (e) => {
      if (!isDragging) return;
      
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;
      const fabSize = 64; // Width and height of the FAB
      
      // Calculate new position with boundaries
      let newX = e.clientX - dragOffset.x;
      let newY = e.clientY - dragOffset.y;
      
      // Keep within viewport
      newX = Math.max(10, Math.min(windowWidth - fabSize - 10, newX));
      newY = Math.max(10, Math.min(windowHeight - fabSize - 10, newY));
      
      setPosition({ x: newX, y: newY });
    };

    const handleDragEnd = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('mouseleave', handleDragEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('mouseleave', handleDragEnd);
    };
  }, [isDragging, dragOffset]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setInput('');
    setIsLoading(true);

    try {
      const res = await api.post('/chatbot/chat', {
        message: userMessage,
        session_id: sessionId
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      if (err.response && err.response.status !== 401) {
          setMessages(prev => [...prev, { role: 'assistant', content: 'I am unable to connect to the server right now. Please try again later.' }]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Get position for chat window
  const getChatWindowPosition = () => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const fabSize = 64;
    const chatWidth = 360;
    const chatHeight = 550;
    
    let x = position.x + fabSize + 10;
    let y = position.y;
    
    // Adjust if chat window goes off screen
    if (x + chatWidth > windowWidth - 10) {
      x = position.x - chatWidth - 10;
    }
    
    if (y + chatHeight > windowHeight - 10) {
      y = windowHeight - chatHeight - 10;
    }
    
    if (y < 10) y = 10;
    if (x < 10) x = 10;
    
    return { x, y };
  };

  const chatPos = isOpen ? getChatWindowPosition() : { x: 0, y: 0 };

  return (
    <>
      {/* Draggable Chat Button */}
      <Fab 
        ref={fabRef}
        color="primary" 
        onClick={() => !isDragging && setIsOpen(!isOpen)}
        onMouseDown={handleDragStart}
        sx={{ 
          position: 'fixed', 
          left: position.x, 
          top: position.y,
          zIndex: 1000,
          boxShadow: '0px 8px 16px rgba(0,0,0,0.15)',
          cursor: isDragging ? 'grabbing' : 'grab',
          transition: isDragging ? 'none' : 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: '0px 12px 24px rgba(0,0,0,0.25)'
          }
        }}
      >
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </Fab>

      {/* Chat Window */}
      {isOpen && (
        <Paper 
          elevation={12}
          sx={{
            position: 'fixed', 
            left: chatPos.x,
            top: chatPos.y,
            width: 360, 
            height: 550,
            display: 'flex', 
            flexDirection: 'column', 
            zIndex: 1000, 
            borderRadius: 4, 
            overflow: 'hidden', 
            border: '1px solid #e2e8f0'
          }}
        >
          {/* Gradient Header - Now also draggable */}
          <Box 
            sx={{ 
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', 
              color: 'white', 
              p: 2, 
              display: 'flex', 
              alignItems: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' }
            }}
            onMouseDown={(e) => {
              // Allow dragging the chat window by its header
              const rect = e.currentTarget.getBoundingClientRect();
              setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
              });
              setIsDragging(true);
            }}
          >
            <BotIcon sx={{ mr: 1.5, fontSize: 28 }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                Transact HR Assistant
              </Typography>
              <Typography variant="caption" sx={{ color: '#e2e8f0' }}>
                Always here to help
              </Typography>
            </Box>
          </Box>

          {/* Chat Body */}
          <Box sx={{ flexGrow: 1, p: 2, overflowY: 'auto', bgcolor: '#f8fafc' }}>
            {messages.map((msg, idx) => (
              <Box key={idx} sx={{ 
                display: 'flex', mb: 2.5,
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
              }}>
                {msg.role === 'assistant' && (
                  <Avatar sx={{ bgcolor: '#3b82f6', width: 32, height: 32, mr: 1.5, boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
                    <BotIcon fontSize="small" />
                  </Avatar>
                )}
                
                <Box sx={{
                  maxWidth: '75%', p: 1.5, px: 2,
                  bgcolor: msg.role === 'user' ? '#3b82f6' : 'white',
                  color: msg.role === 'user' ? 'white' : '#1e293b',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                  whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: 1.5,
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  border: msg.role === 'assistant' ? '1px solid #e2e8f0' : 'none'
                }}>
                  {formatMessage(msg.content)}
                </Box>
              </Box>
            ))}
            
            {/* Typing Indicator */}
            {isLoading && (
              <Box sx={{ display: 'flex', mb: 2, justifyContent: 'flex-start' }}>
                <Avatar sx={{ bgcolor: '#3b82f6', width: 32, height: 32, mr: 1.5, boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
                  <BotIcon fontSize="small" />
                </Avatar>
                <Box sx={{ 
                  p: 1.5, px: 2, bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 0.5,
                  borderRadius: '16px 16px 16px 4px', border: '1px solid #e2e8f0', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                }}>
                  <Box sx={{ width: 6, height: 6, bgcolor: '#94a3b8', borderRadius: '50%', animation: `${typing} 1.4s infinite ease-in-out both`, animationDelay: '-0.32s' }} />
                  <Box sx={{ width: 6, height: 6, bgcolor: '#94a3b8', borderRadius: '50%', animation: `${typing} 1.4s infinite ease-in-out both`, animationDelay: '-0.16s' }} />
                  <Box sx={{ width: 6, height: 6, bgcolor: '#94a3b8', borderRadius: '50%', animation: `${typing} 1.4s infinite ease-in-out both` }} />
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Divider sx={{ borderColor: '#e2e8f0' }} />
          
          {/* Input Area */}
          <Box sx={{ p: 1.5, bgcolor: 'white', display: 'flex', alignItems: 'center' }}>
            <TextField
              inputRef={inputRef}
              fullWidth size="small" placeholder="Ask about leaves, shifts..."
              variant="outlined" value={input}
              onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyPress}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  borderRadius: 6,
                  bgcolor: '#f8fafc',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#cbd5e1' },
                  '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                } 
              }}
            />
            <IconButton 
              color="primary" 
              onClick={handleSend} 
              disabled={!input.trim() || isLoading} 
              sx={{ 
                ml: 1, 
                bgcolor: input.trim() ? '#eff6ff' : 'transparent',
                '&:hover': { bgcolor: '#dbeafe' }
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default ChatbotApplet;